import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Stack,
  Typography,
  Alert,
  Chip,
  Tooltip,
  Checkbox,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from '@mui/material';
import {
  CheckCircle as ValidIcon,
  ErrorOutline as InvalidIcon,
  DocumentScanner as ScanIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import { EasyCheckDialog, EasyCheckButton } from '../../common/Controls';
import { chequeService } from '../../../services/cheques/chequeService';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';

const ACCEPTED = '.pdf,.png,.jpg,.jpeg';

// A voucher row is valid (and selectable) when it has a payee and a positive amount.
const rowErrors = (v) => {
  const errors = [];
  if (!v.beneficiaryName || v.beneficiaryName.trim().length < 2) errors.push('Beneficiary missing');
  if (!(Number(v.amount) > 0)) errors.push('Amount missing or zero');
  if (!v.date) errors.push('Date missing — fill it in the grid');
  return errors;
};

const PaymentVoucherImportDialog = ({ open, onClose, onComplete, bankId, currency }) => {
  const [stage, setStage] = useState('upload'); // 'upload' | 'extracting' | 'preview'
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [vouchers, setVouchers] = useState([]); // [{ beneficiaryName, amount, date, remarks, confidence, _errors }]
  const [excludedIdx, setExcludedIdx] = useState(() => new Set());
  const fileInputRef = useRef(null);

  const reset = () => {
    setStage('upload');
    setFile(null);
    setError('');
    setVouchers([]);
    setExcludedIdx(new Set());
  };

  const handleClose = () => {
    if (stage === 'extracting') return; // don't close mid-extraction
    reset();
    onClose();
  };

  const pickFile = (f) => {
    if (!f) return;
    const name = (f.name || '').toLowerCase();
    const ok = ACCEPTED.split(',').some((ext) => name.endsWith(ext));
    if (!ok) {
      setError('Unsupported file. Upload a PDF or an image (PNG/JPEG).');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('File exceeds the 10MB limit.');
      return;
    }
    setError('');
    setFile(f);
  };

  const handleExtract = useCallback(async () => {
    if (!file) {
      setError('Please choose a voucher file first.');
      return;
    }
    if (!bankId) {
      setError('Please select a bank account before scanning.');
      return;
    }
    setStage('extracting');
    setError('');
    try {
      const result = await chequeService.extractPaymentVouchers(file, bankId);
      if (result?.success) {
        const data = result.data || {};
        const list = data.vouchers || data.Vouchers || [];
        const mapped = list.map((v) => {
          const row = {
            beneficiaryName: v.beneficiaryName ?? v.BeneficiaryName ?? '',
            amount: Number(v.amount ?? v.Amount ?? 0) || 0,
            date: v.date ?? v.Date ?? '',
            chequeNumber: (v.chequeNumber ?? v.ChequeNumber ?? '').toString().trim(),
            remarks: v.remarks ?? v.Remarks ?? '',
            confidence: Number(v.confidence ?? v.Confidence ?? 0) || 0,
          };
          row._errors = rowErrors(row);
          return row;
        });
        setVouchers(mapped);
        // Pre-exclude rows that can't become cheques (no payee + no amount handled server-side, but guard here too)
        setExcludedIdx(new Set());
        setStage('preview');
        if (mapped.length === 0) {
          setError('No payment vouchers were detected in this file.');
        }
      } else {
        setError(result?.message || 'Failed to read the voucher.');
        setStage('upload');
      }
    } catch (err) {
      setError(err?.message || 'Failed to read the voucher.');
      setStage('upload');
    }
  }, [file, bankId]);

  const toggleRow = (idx) => {
    setExcludedIdx((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const includedRows = useMemo(
    () => vouchers.filter((v, idx) => !excludedIdx.has(idx) && v._errors.every((e) => e.startsWith('Date'))),
    [vouchers, excludedIdx]
  );
  // includable = selected AND has payee + amount (date-only warnings are allowed through; user fixes date in grid)
  const includedCount = includedRows.length;
  const totalAmount = includedRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  const handleConfirm = () => {
    if (includedCount === 0) return;
    // Cheque numbers intentionally NOT set — backend auto-generates the next sequential at create time.
    const rows = includedRows.map((r) => ({
      // Use the cheque number printed on the voucher when present; otherwise leave blank
      // so the backend auto-generates the next sequential number at create time.
      chequeNumber: r.chequeNumber || '',
      beneficiaryName: r.beneficiaryName,
      chqAmount: Number(r.amount) || 0,
      chqDate: r.date || '',
      remarks: r.remarks || '',
      source: 'voucher',
    }));
    onComplete({ rows, file });
    handleClose();
  };

  const renderUploadStage = () => (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: 0 }}>
        Upload a <strong>payment voucher</strong> — a single voucher or a multi-page PDF with several vouchers.
        Each voucher becomes one cheque (payee, amount, date) on the currently selected bank.
        <br />
        Cheque numbers are <strong>auto-generated</strong> at creation time.
      </Alert>

      <Paper
        variant="outlined"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          pickFile(e.dataTransfer?.files?.[0]);
        }}
        sx={{
          borderRadius: 0,
          borderStyle: 'dashed',
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: '#F7F9FC',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        <UploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {file ? file.name : 'Click to choose a file or drag it here'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          PDF or image (PNG/JPEG) · max 10MB
        </Typography>
        {/* No `capture` attribute — on iOS that forces the camera and blocks the document picker. */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          hidden
          onChange={(e) => pickFile(e.target.files?.[0])}
        />
      </Paper>

      {error && <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>}
    </Stack>
  );

  const renderExtractingStage = () => (
    <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Reading the voucher with AI… this can take a moment for multi-page PDFs.
      </Typography>
    </Stack>
  );

  const renderPreviewStage = () => (
    <Stack spacing={2}>
      {error && <Alert severity="warning" sx={{ borderRadius: 0 }}>{error}</Alert>}
      {vouchers.length > 0 && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          Review the extracted vouchers. Untick any you don't want, then add them to the grid — you can still edit
          each row's amount, date, and payee in the grid before creating cheques.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, maxHeight: 380 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50, fontWeight: 600 }}>Add</TableCell>
              <TableCell sx={{ width: 50, fontWeight: 600 }}></TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Beneficiary</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Cheque #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Remarks</TableCell>
              <TableCell align="right" sx={{ width: 90, fontWeight: 600 }}>Confidence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vouchers.map((row, idx) => {
              const blocking = row._errors.filter((e) => !e.startsWith('Date'));
              const addable = blocking.length === 0;
              const checked = addable && !excludedIdx.has(idx);
              return (
                <TableRow key={idx} sx={{ backgroundColor: addable ? 'inherit' : 'rgba(211, 47, 47, 0.04)' }}>
                  <TableCell>
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={!addable}
                      onChange={() => toggleRow(idx)}
                    />
                  </TableCell>
                  <TableCell>
                    {row._errors.length === 0 ? (
                      <ValidIcon fontSize="small" sx={{ color: 'success.main' }} />
                    ) : (
                      <Tooltip title={row._errors.join('; ')} arrow>
                        <InvalidIcon fontSize="small" sx={{ color: addable ? 'warning.main' : 'error.main', cursor: 'help' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{row.beneficiaryName || <em style={{ color: '#999' }}>—</em>}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {row.chequeNumber || <em style={{ color: '#999' }}>auto</em>}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {row.date || <em style={{ color: '#999' }}>—</em>}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                    {row.amount > 0 ? formatCurrencyAmount(row.amount, currency, { withSymbol: false }) : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>{row.remarks}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {row.confidence ? `${row.confidence}%` : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {vouchers.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 1 }}>
          <Chip
            label={`Selected: ${includedCount} / ${vouchers.length}`}
            color={includedCount > 0 ? 'success' : 'warning'}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Total: ${currency || ''} ${formatCurrencyAmount(totalAmount, currency, { withSymbol: false })}`.trim()}
            size="small"
            variant="outlined"
          />
        </Stack>
      )}
    </Stack>
  );

  const dialogActions =
    stage === 'preview' ? (
      <>
        <EasyCheckButton buttonType="form" variant="outlined" onClick={reset}>
          ← Upload Another
        </EasyCheckButton>
        <EasyCheckButton
          buttonType="action"
          variant="contained"
          color="success"
          onClick={handleConfirm}
          disabled={includedCount === 0}
        >
          Add to Grid ({includedCount})
        </EasyCheckButton>
      </>
    ) : (
      <>
        <EasyCheckButton buttonType="form" variant="outlined" onClick={handleClose} disabled={stage === 'extracting'}>
          Cancel
        </EasyCheckButton>
        <EasyCheckButton
          buttonType="action"
          variant="contained"
          onClick={handleExtract}
          disabled={stage === 'extracting' || !file || !bankId}
          startIcon={stage === 'extracting' ? <CircularProgress size={18} color="inherit" /> : <ScanIcon />}
        >
          {stage === 'extracting' ? 'Scanning…' : 'Scan Voucher'}
        </EasyCheckButton>
      </>
    );

  return (
    <EasyCheckDialog
      open={open}
      onClose={handleClose}
      title={stage === 'preview' ? 'Review Extracted Vouchers' : 'Scan Payment Voucher'}
      titleIcon={<ScanIcon />}
      variant="large"
      maxWidth="lg"
      fullWidth
      actions={dialogActions}
      disableBackdropClick
    >
      {stage === 'upload' && renderUploadStage()}
      {stage === 'extracting' && renderExtractingStage()}
      {stage === 'preview' && renderPreviewStage()}
    </EasyCheckDialog>
  );
};

export default PaymentVoucherImportDialog;
